// Progress-export PDF data model — the claims-clean document model a caregiver
// can export for one child, as locked by docs/revamp/05_PORTAL_DESIGN.md
// (§2 "Progress export": *Calm, strengths-based, claims-clean PDF; "not a
// diagnostic tool" guardrail; only with the owner's explicit consent*) and
// decided in Q3.5 (client-side / in-browser export, no server PDF service).
//
// This module is the PURE data layer only. The @react-pdf renderer is wired
// later (task 616); here we just shape an {@link ExportModel} that the renderer
// consumes. No dependencies, no I/O — the same input always yields the same
// model.
//
// The non-negotiables this module enforces — the same ones the guard tests assert:
//   • The mandatory guardrail line is ALWAYS present and verbatim. Zooylo is a
//     skill-building companion, not a therapy/assessment/diagnostic tool; it
//     shows engagement, never scores. (Honours docs/CLAIMS_POLICY.md.)
//   • Engagement only — NEVER scores. Since the progress views (design §7) the
//     export IS the Support One-Pager: how best to support the child, practice
//     at home, steps new since the last review, steps being worked on, and each
//     area's own start/end counts. The old per-domain "well / worth-exploring"
//     label — an accuracy threshold presented as a judgement — is gone. Recent
//     play carries play time, never an accuracy/score/percentage/ranking.
//     {@link assertModelClean} walks the model against an allowlist, a
//     banned-token scan AND the progress views' banned-word guard, so a
//     regression that smuggles a score in fails loudly instead of leaking.
//   • A child appears only as the owner-set `displayLabel` (pseudonymous; no PII).
//   • An export REQUIRES the owner's explicit export consent — the requester must
//     hold the `export` scope (owners hold it implicitly; viewers must be granted).

import type {
  Child,
  Id,
  IsoTimestamp,
  SessionSummary,
  SkillStepState,
  SkillSummary,
  SupportProfile,
} from "./types";
import type { ConsentState } from "./consent";
import { canViewerSee } from "./consent";
import { buildOnePager, type OnePagerModel, type Period } from "../progress/onePager";
import { assertProgressClean } from "../progress/claimsGuard";
import {
  bedLabel,
  formatDay,
  lastPractisedLine,
  practiceLines,
  stageLabel,
  stepLine,
  supportLines,
} from "../progress/text";
import { fill, getDictionary, type Locale } from "../i18n/dictionary";

// --- The mandatory guardrail (verbatim, always present) ----------------------

/**
 * The claims-clean guardrail line that MUST appear on every export. It is the
 * single source of truth: {@link assertModelClean} asserts the model's guardrail
 * is exactly this string, so it can never be dropped or weakened. The "never
 * scores" wording legitimately contains the word "scores", so the guardrail line
 * itself is exempt from the banned-token scan applied to the data sections.
 */
export const GUARDRAIL_LINE =
  "Zooylo is a skill-building companion — not a therapy, assessment or " +
  "diagnostic tool. It shows engagement only, never scores.";

/** The export's fixed, plain-language title. Sentence case, no emoji (DS). */
export const EXPORT_TITLE = "Progress summary";

// --- Inputs ------------------------------------------------------------------

/** Inclusive date range the export covers (ISO-8601 UTC bounds). */
export interface DateRange {
  from: IsoTimestamp;
  to: IsoTimestamp;
}

/** The engagement summaries available for the child being exported. */
export interface ExportSummaries {
  sessions: readonly SessionSummary[];
  /** Legacy per-domain summaries. Accepted for older callers, no longer exported. */
  skills?: readonly SkillSummary[];
  /** The child's synced skill-step snapshot (progress scope). */
  stepStates?: readonly SkillStepState[];
  supportProfile?: SupportProfile | null;
}

/** Language and the One-Pager period (default: the last 13 weeks ending `now`). */
export interface ExportOptions {
  locale?: Locale;
  now?: Date;
  period?: Period;
}

/**
 * The consent gate for an export. `state` is the child's consent record and
 * `requestedBy` is the account asking to export. Owners hold the `export` scope
 * implicitly; viewers must have been granted it by the owner.
 */
export interface ExportConsent {
  state: ConsentState;
  requestedBy: Id;
}

// --- Model row shapes (claims-clean by construction) -------------------------

/** One plain line ("Captions on", "Played on 12 days"). */
export interface TextLine {
  text: string;
}

/** A step new since the last review. */
export interface NewStepRow {
  step: string;
  stage: string;
  canDo: string;
  reached: string;
  lastPlayed: string;
}

/** A step being worked on ("With a little help"). */
export interface WorkingStepRow {
  step: string;
  canDo: string;
  since: string;
  lastPlayed: string;
}

/** One area's steps with no game help at the start and end of the period. */
export interface AreaRow {
  area: string;
  start: number;
  end: number;
}

/**
 * One recent play session as it may appear on an export: which game, when, and
 * how long the child played. `duration` is human-readable play time (engagement);
 * the source `accuracy` signal is deliberately dropped and never reaches here.
 */
export interface RecentSession {
  gameId: string;
  at: IsoTimestamp;
  /** Human-readable play time, e.g. "9 min". Engagement, never a score. */
  duration: string;
}

/** Export header: a fixed title plus the owner-set, pseudonymous child label. */
export interface ExportHeader {
  title: string;
  /** Owner-set display label — the ONLY child identifier, rendered client-side. */
  childLabel: string;
}

/** The full claims-clean export document model the renderer consumes. */
export interface ExportModel {
  header: ExportHeader;
  range: DateRange;
  /** The One-Pager period line, e.g. "Last 13 weeks: 22 Jul 2026 to 21 Oct 2026". */
  period: string;
  support: TextLine[];
  practice: TextLine[];
  newSince: NewStepRow[];
  workingOn: WorkingStepRow[];
  areas: AreaRow[];
  sessions: RecentSession[];
  /** "What we saw during play at home. Not an assessment, screening or diagnosis." */
  footer: string;
  /** The mandatory guardrail line — always {@link GUARDRAIL_LINE}. */
  guardrail: string;
}

// --- Allowlist + banned tokens (the "engagement, never scores" guarantee) ----

/**
 * The EXACT, exhaustive set of field names each row-section may carry. Anything
 * outside this set is rejected by {@link assertModelClean}. Neither section may
 * grow a numeric `accuracy`, `level`, `score`, or similar field.
 */
export const MODEL_ALLOWLIST = {
  support: ["text"],
  practice: ["text"],
  newSince: ["step", "stage", "canDo", "reached", "lastPlayed"],
  workingOn: ["step", "canDo", "since", "lastPlayed"],
  areas: ["area", "start", "end"],
  sessions: ["gameId", "at", "duration"],
} as const satisfies Record<string, readonly string[]>;

type ModelSection = keyof typeof MODEL_ALLOWLIST;

const MODEL_SECTIONS = Object.keys(MODEL_ALLOWLIST) as ModelSection[];

/**
 * Case-insensitive substrings that must never appear in any exported string
 * value or key. These are the score/percentage/ranking vocabulary an export is
 * forbidden from leaking. (The guardrail line, which legitimately says "never
 * scores", is checked separately and exempt from this scan.)
 */
export const BANNED_MODEL_TOKENS = [
  "score",
  "percent",
  "%",
  "rank",
  "accuracy",
  "grade",
  "out of",
] as const;

function assertNoBannedToken(value: unknown, where: string): void {
  if (typeof value !== "string") {
    return;
  }
  const lower = value.toLowerCase();
  for (const token of BANNED_MODEL_TOKENS) {
    if (lower.includes(token)) {
      throw new Error(
        `Export ${where} value "${value}" contains the banned token "${token}" — ` +
          `an export shows engagement only, never scores/percentages/rankings.`,
      );
    }
  }
}

/**
 * Throws unless the model is claims-clean:
 *   1. the guardrail line is present and exactly {@link GUARDRAIL_LINE};
 *   2. every row carries only allowlisted fields (no smuggled score/accuracy);
 *   3. no exported string value or key contains a banned score/ranking token.
 * This is the backstop: a future change that tries to surface a score fails here
 * instead of shipping it.
 */
export function assertModelClean(model: ExportModel): void {
  if (model.guardrail !== GUARDRAIL_LINE) {
    throw new Error(
      "The claims-clean guardrail line is missing or altered — it must be present " +
        "verbatim on every export.",
    );
  }

  for (const section of MODEL_SECTIONS) {
    const allowed: readonly string[] = MODEL_ALLOWLIST[section];
    const rows = model[section] as unknown as ReadonlyArray<Record<string, unknown>>;
    for (const row of rows) {
      for (const key of Object.keys(row)) {
        if (!allowed.includes(key)) {
          throw new Error(
            `Export field "${key}" in section "${section}" is not in the allowlist ` +
              `[${allowed.join(", ")}] — an export must never carry scores or outcomes.`,
          );
        }
        assertNoBannedToken(key, `${section} field name`);
        assertNoBannedToken(row[key], `${section}.${key}`);
      }
    }
  }

  assertNoBannedToken(model.header.title, "header.title");
  assertNoBannedToken(model.header.childLabel, "header.childLabel");
  assertNoBannedToken(model.period, "period");
  assertNoBannedToken(model.footer, "footer");

  // The progress views' wider list (mastered, behind, delay, expected, …),
  // over everything except the guardrail line, which says "never scores".
  const { guardrail: _guardrail, ...rest } = model;
  void _guardrail;
  assertProgressClean(rest, "Export");
}

// --- Build -------------------------------------------------------------------

/**
 * Assemble the claims-clean {@link ExportModel} for one child over `range`.
 *
 * Requires the owner's explicit export consent: `consent.requestedBy` must hold
 * the `export` scope for this child (owners hold it implicitly; viewers must
 * have been granted it). Throws otherwise — an export is never produced without
 * consent.
 *
 * The result is engagement-only by construction (no scores/accuracy are copied
 * across) and is run through {@link assertModelClean} before return, so it is
 * guaranteed clean and carries the mandatory guardrail line.
 */
export function buildExportModel(
  child: Child,
  summaries: ExportSummaries,
  range: DateRange,
  consent: ExportConsent,
  options: ExportOptions = {},
): ExportModel {
  assertExportConsent(child, consent);

  const locale: Locale = options.locale ?? "en";
  const p = getDictionary(locale).service.progress;
  const childSessions = summaries.sessions.filter((s) => s.childId === child.id);

  const onePager: OnePagerModel = buildOnePager({
    stepStates: (summaries.stepStates ?? []).filter((s) => s.childId === child.id),
    sessions: childSessions,
    supportProfile:
      summaries.supportProfile && summaries.supportProfile.childId === child.id
        ? summaries.supportProfile
        : null,
    period: options.period,
    now: options.now,
    locale,
  });

  const sessions: RecentSession[] = childSessions
    .filter((s) => inRange(s.at, range))
    // Most recent first; ISO-8601 UTC strings sort lexicographically by time.
    .slice()
    .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0))
    .map((s) => ({
      gameId: s.gameId,
      at: s.at,
      duration: formatDuration(s.durationSeconds),
    }));

  const model: ExportModel = {
    header: { title: EXPORT_TITLE, childLabel: child.displayLabel },
    range: { from: range.from, to: range.to },
    period: fill(p.period, {
      from: formatDay(locale, onePager.period.from),
      to: formatDay(locale, onePager.period.to),
    }),
    support: supportLines(p, onePager).map((text) => ({ text })),
    practice: practiceLines(p, onePager).map((text) => ({ text })),
    newSince: onePager.newSince.map((s) => ({
      step: stepLine(p, s),
      stage: stageLabel(p, s.stage),
      canDo: s.canDo,
      reached: fill(p.reachedOn, { date: formatDay(locale, s.reachedOn) }),
      lastPlayed: lastPractisedLine(p, locale, s.lastPractisedOn),
    })),
    workingOn: onePager.workingOn.map((s) => ({
      step: stepLine(p, s),
      canDo: s.canDo,
      since: s.since ? fill(p.since, { date: formatDay(locale, s.since) }) : "",
      lastPlayed: lastPractisedLine(p, locale, s.lastPractisedOn),
    })),
    areas: onePager.areas.map((a) => ({ area: bedLabel(p, a.domain), start: a.start, end: a.end })),
    sessions,
    footer: getDictionary(locale).service.childDetail.footer,
    guardrail: GUARDRAIL_LINE,
  };

  assertModelClean(model);
  return model;
}

/** Guard: an export requires the owner's explicit export consent. */
function assertExportConsent(child: Child, consent: ExportConsent): void {
  if (consent.state.childId !== child.id) {
    throw new Error(
      "Consent record does not match the child being exported.",
    );
  }
  if (!canViewerSee(consent.state, consent.requestedBy, "export")) {
    throw new Error(
      "A progress export requires the owner's explicit export consent.",
    );
  }
}

/** Inclusive range test on ISO-8601 UTC timestamps (lexicographic = temporal). */
function inRange(at: IsoTimestamp, range: DateRange): boolean {
  return at >= range.from && at <= range.to;
}

/** Render a session length as human-readable play time, e.g. "9 min". */
function formatDuration(seconds: number): string {
  const minutes = Math.max(0, Math.round(seconds / 60));
  return `${minutes} min`;
}
